import { useMutation, useQueryClient } from "@tanstack/react-query";
import { z } from "zod/v4";

import { useProfile } from "./get-profile";
import { updateUser } from "./utils";

import type { MutationConfig } from "@/lib/react-query";
import type { User } from "@/types/user";

export const addressSchema = z.object({
  address: z.string().optional(),
  city: z.string().optional(),
  country: z.string().optional(),
  postal_code: z.string().optional(),
});

// Aggiungiamo `index` per sapere quale indirizzo aggiornare
export type UpdateAddressInput = z.infer<typeof addressSchema> & { index: number };

type UseUpdateAddressOptions = {
  mutationConfig?: MutationConfig<(data: UpdateAddressInput) => Promise<User>>;
};

export const useUpdateAddress = ({ mutationConfig }: UseUpdateAddressOptions = {}) => {
  const queryClient = useQueryClient();
  const { data: user } = useProfile();

  const { onSuccess, ...restConfig } = mutationConfig || {};

  return useMutation({
    mutationFn: ({ index, ...data }: UpdateAddressInput) => {
      if (!user) throw new Error("User data not available");

      const updatedAddresses = [...(user.additional_info?.addresses || [])];
      updatedAddresses[index] = { ...updatedAddresses[index], ...data };

      const request: Partial<User> = {
        additional_info: {
          ...user.additional_info,
          addresses: updatedAddresses,
        },
      };

      return updateUser(request);
    },
    onSuccess: (data, variables, onMutateResult, context) => {
      queryClient.setQueryData(["profile"], data);
      onSuccess?.(data, variables, onMutateResult, context);
    },
    ...restConfig,
  });
};
